import {h, Fragment} from "preact"
import { usePagination, useActions } from "@nosto/preact"
import Icon from "./elements/Icon"

function PageLink({ onClick, href, className, ariaLabel, children }) {
    return (
        <a
            class={`text-black ${className}`}
            href={href}
            aria-label={ariaLabel}
            onClick={onClick}
        >
            {children}
        </a>
    )
}

export default function Pagination() {
    const { prev, first, pages, last, next } = usePagination({ width: 5 });
    
    if(pages?.length < 2) return;

    const { updateSearch, simulateUpdateSearch } = useActions();

    function pageLinkProps({ from }) {
        return {
            onClick: e => {
                e.preventDefault()
                updateSearch({ products: { from } })
                scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            },
            href: simulateUpdateSearch({ products: { from } }).url,
        }
    }

    return (
        <div className="py-[100px]">
            <div className="flex justify-center">
                <nav 
                    className="border border-grey-2 rounded-md py-3 px-4" 
                    role="navigation" 
                    aria-label="Pagination"
                >
                    <ul class="list-none flex items-center justify-center gap-3 m-0 p-0">
                        <li className="m-0 p-0 mr-10">
                            {prev ? (
                                <PageLink {...pageLinkProps(prev)} className="" ariaLabel="Previous page">
                                    <svg class="" width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.9375 16.7266L2.0625 10.1641C1.86719 9.96875 1.75 9.73437 1.75 9.46094C1.75 9.22656 1.86719 8.99219 2.0625 8.79687L8.9375 2.23437C9.28906 1.88281 9.91406 1.88281 10.2656 2.27344C10.6172 2.625 10.6172 3.25 10.2266 3.60156L5.03125 8.52344L18.3125 8.52344C18.8594 8.52344 19.25 8.95312 19.25 9.46094C19.25 10.0078 18.8594 10.3984 18.3125 10.3984L5.03125 10.3984L10.2266 15.3594C10.6172 15.7109 10.6172 16.3359 10.2656 16.6875C9.91406 17.0781 9.32813 17.0781 8.9375 16.7266Z" fill="currentColor"></path>
                                    </svg>
                                </PageLink>
                            ) : (
                                <span className="text-black opacity-60" aria-hidden="true">
                                    <svg class="" width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.9375 16.7266L2.0625 10.1641C1.86719 9.96875 1.75 9.73437 1.75 9.46094C1.75 9.22656 1.86719 8.99219 2.0625 8.79687L8.9375 2.23437C9.28906 1.88281 9.91406 1.88281 10.2656 2.27344C10.6172 2.625 10.6172 3.25 10.2266 3.60156L5.03125 8.52344L18.3125 8.52344C18.8594 8.52344 19.25 8.95312 19.25 9.46094C19.25 10.0078 18.8594 10.3984 18.3125 10.3984L5.03125 10.3984L10.2266 15.3594C10.6172 15.7109 10.6172 16.3359 10.2656 16.6875C9.91406 17.0781 9.32813 17.0781 8.9375 16.7266Z" fill="currentColor"></path>
                                    </svg>
                                </span>
                            )}
                        </li>

                        {first && (
                            <>
                                <li className="m-0 p-0">
                                    <PageLink {...pageLinkProps(first)} ariaLabel="Previous page" className="no-underline">
                                        {first.page}
                                    </PageLink>
                                </li>
                                <li><span class="text-base text-black">...</span></li>
                            </>
                        )}

                        {pages.map(page => (
                            <li class="m-0 p-0">
                                <PageLink className={`${page.current ? "underline" : "no-underline"}`} ariaLabel={`${page.page} page`} {...pageLinkProps(page)}>
                                    {page.page}
                                </PageLink>
                            </li>
                        ))}

                        {last && (
                            <>
                                <li className="m-0 p-0"><span class="text-base text-black">...</span></li>
                                <li className="m-0 p-0">
                                    <PageLink {...pageLinkProps(last)} ariaLabel="Last page" className="no-underline">
                                        {last.page}
                                    </PageLink>
                                </li>
                            </>
                        )}

                        <li className="m-0 p-0 ml-10">
                            {next ? (
                                <PageLink {...pageLinkProps(next)} className="" ariaLabel="Next page">
                                    <svg class="" width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12.0625 3.27344L18.9375 9.83594C19.1328 10.0312 19.25 10.2656 19.25 10.5391C19.25 10.7734 19.1328 11.0078 18.9375 11.2031L12.0625 17.7656C11.7109 18.1172 11.0859 18.1172 10.7344 17.7266C10.3828 17.375 10.3828 16.75 10.7734 16.3984L15.9688 11.4766H2.6875C2.14062 11.4766 1.75 11.0469 1.75 10.5391C1.75 9.99219 2.14062 9.60156 2.6875 9.60156H15.9688L10.7734 4.64062C10.3828 4.28906 10.3828 3.66406 10.7344 3.3125C11.0859 2.92188 11.6719 2.92188 12.0625 3.27344Z" fill="currentColor"></path>
                                    </svg>
                                </PageLink>
                            ) : (
                                <span className="text-black opacity-60">
                                    <svg class="" width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12.0625 3.27344L18.9375 9.83594C19.1328 10.0312 19.25 10.2656 19.25 10.5391C19.25 10.7734 19.1328 11.0078 18.9375 11.2031L12.0625 17.7656C11.7109 18.1172 11.0859 18.1172 10.7344 17.7266C10.3828 17.375 10.3828 16.75 10.7734 16.3984L15.9688 11.4766H2.6875C2.14062 11.4766 1.75 11.0469 1.75 10.5391C1.75 9.99219 2.14062 9.60156 2.6875 9.60156H15.9688L10.7734 4.64062C10.3828 4.28906 10.3828 3.66406 10.7344 3.3125C11.0859 2.92188 11.6719 2.92188 12.0625 3.27344Z" fill="currentColor"></path>
                                    </svg>
                                </span>
                            )}
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    )
}